import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App.tsx';
import { AppErrorFallback } from './components/errors/AppErrorFallback';

// Some WebViews (Instagram, older Android) expose performance but not getEntriesByType
if (typeof globalThis.performance !== 'undefined' && typeof globalThis.performance.getEntriesByType !== 'function') {
  (globalThis.performance as any).getEntriesByType = () => [];
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN && !import.meta.env.DEV,
  environment: import.meta.env.MODE,
  ignoreErrors: [
    // Chrome browser/extension Payment Request API noise — not application code
    /Object Not Found Matching Id/,
    // Instagram in-app browser Java/WebKit bridge errors — not application code
    /enableButtonsClickedMetaDataLogging/,
    /window\.webkit\.messageHandlers/,
    /Java exception was raised during method invocation/,
    // DuckDuckGo browser extension internal messaging — not application code
    /Invalid call to runtime\.sendMessage/,
    // Stale chunk after deploy — handled by vite:preloadError → reload, not a real error
    /Load failed/,
    /Failed to fetch dynamically imported module/,
    // PayPal Honey and other browser extensions
    /UnavailableError/,
    // Browser extensions using webextension-polyfill injected via blob: URL —
    // runtime.getManifest only exists inside an extension context, never in the page
    /runtime\.getManifest is not a function/,
  ],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: true }),
  ],
  tracesSampleRate: 0.1,
  beforeSend(event) {
    // Drop errors whose stack frames originate from browser extensions
    const frames = event.exception?.values?.flatMap(v => v.stacktrace?.frames ?? []) ?? [];
    const isExtension = frames.some(f => {
      const fn = f.filename ?? '';
      return fn.includes('.appex/') || fn.includes('safariextension/') ||
             fn.includes('chrome-extension://') || fn.includes('moz-extension://') ||
             fn.includes('safari-extension://');
    });
    if (isExtension) return null;
    return event;
  },
  beforeSendTransaction: (event) => {
    if (event.spans) {
      event.spans = event.spans.filter(span => {
        const url = span.data?.['http.url'] ?? span.description ?? '';
        return !String(url).startsWith('blob:');
      });
    }
    return event;
  },
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
});

const container = document.getElementById('root')!;

if (container.children.length > 0) {
  // Server-rendered HTML present — hydrate instead of replace
  // (children excludes comment nodes like <!--ssr-outlet--> on SPA pages)
  hydrateRoot(
    container,
    <StrictMode>
      <Sentry.ErrorBoundary fallback={({ eventId, resetError, error }) => (
        <AppErrorFallback eventId={eventId} resetError={resetError} error={error} />
      )}>
        <App />
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
} else {
  // No SSR content — standard SPA mount
  createRoot(container).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={({ eventId, resetError, error }) => (
        <AppErrorFallback eventId={eventId} resetError={resetError} error={error} />
      )}>
        <App />
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
}

// Signal that hydration/mount is complete so FOUC CSS can fade in
document.body.classList.add('hydrated');

// When Vite chunk hashes change after a deploy, old cached HTML references stale
// chunk filenames. Auto-reload to get fresh HTML. sessionStorage flag prevents
// an infinite loop if the reload itself also fails.
import { registerChunkReloadHandler } from './utils/chunkReloadHandler';
registerChunkReloadHandler();
